<template>
    <!-- Fixed readout row: the values under the cursor (or, at rest, today's), shown above the
         chart instead of in a floating tooltip - a tooltip tall enough to sit on three stacked
         bands plus a comparison halo would cover the very data it describes. Content
         comes from `readout`, a plain prop the chart updates imperatively on hover; the row itself
         has no logic of its own beyond which cells the currently visible bands call for. -->
    <div
        class="mb-3 flex flex-wrap gap-x-8 gap-y-2 rounded-lg border px-4 py-2.5 transition-colors"
        :class="[pinned ? 'border-primary-700 bg-gray-100' : 'border-gray-200 bg-gray-50', camerasExpanded ? 'items-start' : 'items-center']"
    >
        <div class="flex min-w-0 flex-col">
            <!-- Where this reading sits in the batch's own life, which is the vocabulary the x axis
                 beneath it is numbered in - and the one fact in this row that is not a measurement,
                 so it is drawn as a chip in the solution's colour rather than as a seventh figure.
                 It takes the label row the other cells spend on a caption, since a chip reading
                 "Dag 32" needs no caption saying "Date" above it. Absent for an instant no Study
                 Period covers, where the date below stands alone. -->
            <span v-if="periodPosition" :class="PERIOD_CHIP_CLASS">{{ periodPosition }}</span>
            <span v-else :class="READOUT_LABEL_CLASS">{{ $t('views.monitoring.activeGroups.modals.productivity.readoutDate') }}</span>
            <span :class="READOUT_DATE_CLASS">{{ dateLabel }}</span>
            <!-- The reference's name, on the same line as every cell's reference figure - which is
                 what says those muted figures are the reference's rather than a second reading of
                 the Section's own. Named here once rather than repeated across six cells. -->
            <span v-if="reference" :class="REFERENCE_CLASS" class="max-w-[11rem] truncate" :title="reference.name">{{ reference.name }}</span>
            <!-- Opens the per-camera breakdown in the metric cells opposite. Collapsed by default:
                 six cameras add six lines to each of three cells, which is the row growing past the
                 chart above it for a question most readers are not asking - the Section figure is
                 the answer until one of them asks it.

                 Names no camera. The obvious thing was to name them here, once, and let the cells
                 state bare figures in the same order - but the left column carries the date, the
                 reference name and this toggle, and a metric cell carries a label, a value and a
                 reference figure, so the two stacks start at different heights and drift again with
                 every metric that has no reference. Read as a table, which is what two aligned
                 columns are, it paired every figure with the wrong camera's name. A name beside its
                 own figure cannot do that, so that is where the names went.

                 Gone entirely when no camera-reporting metric is on screen: there would be no cell
                 for the breakdown to appear in, and a control whose only visible effect is nothing is
                 worse than no control at all. -->
            <button v-if="cameras.length > 0 && namingMetric !== null" type="button" :class="CAMERA_TOGGLE_CLASS" :aria-expanded="camerasExpanded" @click="emit('toggle-cameras')">
                <ChevronRight class="h-3 w-3 shrink-0 transition-transform" :class="camerasExpanded ? 'rotate-90' : ''" aria-hidden="true" />
                {{ $tc('views.monitoring.activeGroups.modals.productivity.readoutCameras', cameras.length, { count: cameras.length }) }}
            </button>
        </div>
        <!-- One cell per visible metric, in the registry's own declaration order - the same order
             the toggle chips and the chart's own bands are laid out in. Each cell carries its
             series' colour and mark as a leading swatch, so a figure here can be matched to the
             curve it came from without reading the label: the chart has no legend and no floating
             tooltip to make that link anywhere else.

             Driven by the registry rather than written out per metric, because every difference
             between two of these cells - its label, its colour, whether it is drawn as a line or a
             column, what unit it is stated in - is a fact the metric is already declared with. -->
        <div v-for="cell in cells" :key="cell.key" class="flex flex-col">
            <span :class="READOUT_LABEL_CLASS">
                <span :class="cell.shape === 'line' ? LINE_SWATCH_CLASS : COLUMN_SWATCH_CLASS" :style="{ backgroundColor: cell.color }" />
                {{ cell.label }}
            </span>
            <span :class="READOUT_VALUE_CLASS">
                {{ cell.value }}
                <span v-if="cell.unit" :class="UNIT_CLASS">{{ cell.unit }}</span>
                <!-- Water only: the gap between measured and expected, which is what the two water
                     columns are drawn together to show. Stated rather than left to be estimated
                     from two bar heights, and signed, so "more water than expected" and "less" are
                     not the same reading. -->
                <span
                    v-if="cell.key === 'waterNorm' && waterDeviationPercent !== null"
                    class="ml-1.5 text-xs font-semibold"
                    :class="waterDeviationPercent > 0 ? 'text-sky-700' : 'text-gray-400'"
                >
                    {{ waterDeviationPercent > 0 ? '+' : '' }}{{ formattedWaterDeviation }} %
                </span>
            </span>
            <!-- What the reference stood at here, and by how much this Section is above or below
                 it. The gap is the whole reason a reference was picked: read off two curves by eye
                 it is a guess, and at these axis scales a 5 kg difference and a 15 kg one look
                 alike. Signed and in the metric's own unit rather than as a percentage - "more" is
                 good for weight and bad for the dead count, so the figure states the direction and
                 leaves the judgement to the reader. -->
            <span v-if="reference" :class="REFERENCE_CLASS">
                {{ cell.referenceValue }}
                <span v-if="cell.referenceGap !== null" class="ml-0.5 font-semibold text-gray-500">{{ cell.referenceGap }}</span>
            </span>
            <!-- What each camera stood at here, and how far it sits from the Section figure above -
                 the answer to "which camera is dragging this down" as a number. Read off the curves
                 by eye it is a guess: the cameras are a band a couple of pixels deep, which is the
                 whole reason this breakdown exists.

                 Only under the three metrics a camera reports. Metered water and the dead count are
                 facts about the Section, so those cells state nothing here rather than a stack of
                 em-dashes implying a camera declined to answer.

                 Each line is also the control that switches that camera's curves off - the figure the
                 reader is looking at is the thing they click, in whichever cell they happen to be
                 reading. This row is the chart's only legend, and hiding its own entry is what a
                 legend entry does on every chart a reader has met, so a click here takes this camera
                 off the plot and leaves every other one exactly where it was.

                 A hidden camera keeps its line here, dimmed, and keeps stating its figures. It has to
                 stay: it is the only way back on. And its numbers are still the answer to why it was
                 switched off - the reader took its curve off the plot to see the rest, not to stop
                 knowing what it read. -->
            <button
                v-for="camera in cell.cameras"
                :key="camera.ordinal"
                type="button"
                :class="[CAMERA_FIGURE_CLASS, camera.hidden ? CAMERA_FIGURE_HIDDEN_CLASS : '']"
                :aria-pressed="!camera.hidden"
                :title="$t(camera.hidden ? SHOW_TITLE_KEY : HIDE_TITLE_KEY)"
                @click="emit('toggle-camera', camera.sensorId)"
            >
                <!-- The swatch goes grey with the camera rather than merely fading with the rest of
                     the line: dimmed alone reads as "further away", which is what a camera figure
                     already is next to the Section figure above it. Losing the colour is what says
                     the curve that colour stood for is not on the plot. -->
                <span :class="CAMERA_SWATCH_CLASS" :style="{ borderColor: camera.hidden ? HIDDEN_SWATCH_COLOR : camera.color }" />
                <!-- The camera's name, in the leftmost cell drawing cameras at all and nowhere else.
                     Once, because it is the same two words for one device and repeating it under
                     three metrics is three times the width for no second fact; in the leftmost,
                     because that is where a reader meets the camera first and the cells to its right
                     line up with it row for row - all three are built from one roster, in one order,
                     starting at the same line. That is an alignment between cells of identical
                     structure, not between two columns that merely sit side by side. -->
                <span v-if="camera.label !== null" :class="CAMERA_NAME_CLASS">{{ camera.label }}</span>
                {{ camera.value }}
                <span v-if="camera.gap !== null" class="ml-0.5 font-semibold text-gray-500">{{ camera.gap }}</span>
            </button>
        </div>
        <!-- Pushed to the far end so the figures keep their positions whether or not the row is
             held: a control that appears mid-row would shift every value beside it at the exact
             moment the reader pinned them in place to read them. -->
        <!-- `self-center` because the row switches to top alignment once the camera breakdown makes
             the metric cells taller than this control: a button that jumped to the top of a five-line
             row on expand would read as part of the breakdown. -->
        <button
            v-if="pinned"
            type="button"
            class="ml-auto self-center inline-flex items-center gap-1.5 rounded-full bg-primary-300 px-2.5 py-1 text-xs font-semibold text-primary-800 transition-colors hover:bg-primary-700 hover:text-white"
            @click="emit('release')"
        >
            <Pin class="h-3 w-3" aria-hidden="true" />
            {{ $t('views.monitoring.activeGroups.modals.productivity.readoutPinned') }}
            <X class="h-3 w-3" aria-hidden="true" />
        </button>
        <!-- What this reading *is*, when it is not a held one: the point under the cursor, or - once
             the cursor leaves - the latest reading the range holds, which on a Section whose Sensor
             stopped reporting months ago is nowhere near today. Both belong in the row's one state
             slot rather than on the chip, which states identity at every one of the three states. -->
        <span v-else class="ml-auto self-center text-[11px] italic text-gray-400">
            {{ $t(pointing ? PIN_HINT_KEY : RESTING_KEY) }}
        </span>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { ChevronRight, Pin, X } from 'lucide-vue-next';
import { i18n } from '@/plugins/internationalization/i18n';
import type { ProductivityGranularity } from '@/plugins/store/actions/queries/active-groups';
import colors from '@/plugins/tailwind';
import type { CameraReadoutEntry } from './cameraReadout';
import { isCameraMetric } from './cameraSeries';
import { productivityMetricGap, productivityMetricValue, PRODUCTIVITY_VALUE_PLACEHOLDER } from './productivityFormatting';
import { METRIC_KEYS, PRODUCTIVITY_METRICS, type MetricFlags, type MetricKey } from './productivityMetrics';
import { formatReadoutTimestamp, METRIC_FIGURE_FIELD, type ProductivityReadout, type ProductivityReferenceReadout } from './productivityReadout';

const READOUT_LABEL_CLASS = 'flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400';
// The position chip. `primary-300` is the palette's lightest shade - there is no softer brand tint to
// tone this down with - and it is a pale wash of the brand in all four solutions, so `primary-800`
// stays legible on it everywhere. Deliberately not the filled `primary-700` the metric toggles wear:
// this chip is not a control and must not invite the click that would prove it.
const PERIOD_CHIP_CLASS = 'w-fit rounded-full bg-primary-300 px-2 py-0.5 text-[10px] font-bold tracking-[0.3px] text-primary-800';
const READOUT_VALUE_CLASS = 'text-sm font-semibold tabular-nums text-gray-800';
// The date sits where every other cell carries a measurement, but it is not one - it names the point
// the measurements were taken at, which the chip above it has already named in the axis's own terms.
// So it is drawn back: smaller, grey, and at the reference figures' weight rather than the actuals'.
// The line height is held at the value row's own, or this cell would be shorter than the six beside
// it and, being centred, would lift its reference line off the level the other cells share.
const READOUT_DATE_CLASS = 'text-[11px] font-medium leading-[19px] tabular-nums text-gray-500';
const UNIT_CLASS = 'text-xs font-medium text-gray-400';
// The chart carries no legend, so this row is it. A swatch therefore has to say which *mark* on the
// chart a figure came from, not just which colour: a short rule for the metrics drawn as lines, a
// filled block for those drawn as columns. Colour alone is not enough: weight and Daily Gain are two
// shades of one green, and shape survives a reader who cannot separate the two reds or the two greens
// at all.
const LINE_SWATCH_CLASS = 'inline-block h-0.5 w-3.5 shrink-0 rounded-full';
const COLUMN_SWATCH_CLASS = 'inline-block h-2 w-2 shrink-0 rounded-[2px]';
// The reference line under every value, in the same muted weight the halo is drawn in, so the row
// reads as one bold row of actuals over one faint row of reference figures rather than as twelve
// figures of equal standing.
const REFERENCE_CLASS = 'text-[11px] font-medium tabular-nums text-gray-400';
// A camera's figure is subordinate to the Section figure it is read against, so it sits at the reference
// figures' weight - but not their colour, or at 11px the two stacks would read as one list of faint
// numbers with no telling where one ended.
const CAMERA_FIGURE_CLASS = '-mx-1 flex w-fit items-center gap-1.5 rounded px-1 text-[11px] font-medium tabular-nums text-gray-500 transition-all hover:bg-gray-200';
// The breakdown's own toggle, at the label row's size and weight because it captions what the cells
// opposite are showing rather than measuring anything.
const CAMERA_TOGGLE_CLASS = 'mt-0.5 inline-flex w-fit items-center gap-1 rounded text-[10px] font-bold uppercase tracking-[0.5px] text-gray-400 transition-colors hover:text-gray-600';
// A camera whose curves are off the plot: a step back rather than a disappearance, since the entry is both
// the way back on and the explanation for why it was switched off. Hover takes it to full strength, for
// the reader asking what they are about to switch back on.
const CAMERA_FIGURE_HIDDEN_CLASS = 'opacity-50 hover:opacity-100';
// The swatch's colour once its curve is off the plot, at the weight the row's labels are drawn in. A token
// rather than a hex, so it moves with the palette the rest of the row is greyed against.
const HIDDEN_SWATCH_COLOR = colors.gray[400];
// The camera's name sits back from its figure: at equal weight the two words would outrank the number
// they belong to.
const CAMERA_NAME_CLASS = 'max-w-[9rem] truncate font-medium text-gray-400';
// Dashed, because the curve it stands for is dashed: a swatch says which *mark* on the plot a figure came
// from, and a camera is the third mark this chart draws. Wider and heavier than the metric swatches,
// because in the two cells that state no name it is the only thing identifying the camera and a finer dash
// spends most of its width on gaps.
const CAMERA_SWATCH_CLASS = 'inline-block w-4 shrink-0 border-t-[3px] border-dashed';
const PRODUCTIVITY_T = 'views.monitoring.activeGroups.modals.productivity';
const PIN_HINT_KEY = `${PRODUCTIVITY_T}.readoutPinHint`;
const RESTING_KEY = `${PRODUCTIVITY_T}.readoutResting`;
const HIDE_TITLE_KEY = `${PRODUCTIVITY_T}.readoutCameraHide`;
const SHOW_TITLE_KEY = `${PRODUCTIVITY_T}.readoutCameraShow`;

const props = defineProps<{
    readout: ProductivityReadout | null;
    granularity: ProductivityGranularity;
    // Week/day-in-period for the instant `readout` names, or null where no Production Group covers it.
    // Resolved by the chart, which owns the Production Groups and the reference's projection; the row
    // renders the string and decides nothing about it.
    periodPosition: string | null;
    // Whether the row is following the cursor rather than resting on the latest reading. Distinct
    // from `pinned`, which outranks both.
    pointing: boolean;
    // Which metrics currently have a cell here: band-gated, not just the raw toggle, so a metric
    // whose toggle is on but whose band is absent drops out of the row exactly as it drops out of
    // the chart.
    visible: MetricFlags;
    // The comparison reference at the same point in the Production Group, or null when none is picked.
    // Present with null figures rather than absent where the reference has nothing, so the row's
    // height does not change as the cursor crosses the edges of the reference's own run.
    reference: ProductivityReferenceReadout | null;
    // Whether the chart is holding this reading still rather than following the cursor. The row
    // owns none of that state - it reports the release back and the chart decides.
    pinned: boolean;
    // Each of the Section's cameras with what it stated at the instant `readout` names, in ordinal
    // order. Empty on a Section drawing no camera curves, which is what takes the breakdown off the
    // row entirely: on a single-camera Section the Section figure already IS that camera's.
    cameras: CameraReadoutEntry[];
    // Whether the breakdown is open, and which cameras the chart has switched off, by Sensor id.
    // Both held by the chart - the row reports the clicks back and decides neither.
    camerasExpanded: boolean;
    hiddenSensorIds: number[];
}>();

// `toggle-camera` names one camera and not the state it should end in, so a caller flipping the one it is
// told about cannot disagree with the row. By Sensor id - see resolveHiddenCameras.
const emit = defineEmits<{ release: []; 'toggle-cameras': []; 'toggle-camera': [sensorId: number] }>();

// Whether the metric cells are actually stating camera figures - one question, so no cell repeats both
// tests and builds a per-cell breakdown of nothing.
const camerasShown = computed(() => props.camerasExpanded && props.cameras.length > 0);

// The visible camera-reporting metric a reader reaches first, which is the cell that names the cameras.
// Derived from the cells on screen rather than fixed to weight, which is a toggle like any other.
const namingMetric = computed<MetricKey | null>(() => METRIC_KEYS.find((key) => props.visible[key] && isCameraMetric(key)) ?? null);

const dateLabel = computed(() => (props.readout ? formatReadoutTimestamp(props.readout.timestamp) : PRODUCTIVITY_VALUE_PLACEHOLDER));

const cells = computed(() =>
    METRIC_KEYS.filter((key) => props.visible[key]).map((key) => {
        const metric = PRODUCTIVITY_METRICS[key];
        // Null for a metric that is not a measurement at all - the Growth Index is a ratio against
        // a target, so no unit follows its figure.
        const unitKey = metric.unitKey(props.granularity);
        const actual = props.readout?.[METRIC_FIGURE_FIELD[key]] ?? null;
        const reference = props.reference?.[METRIC_FIGURE_FIELD[key]] ?? null;
        return {
            key,
            label: i18n.global.t(metric.labelKey) as string,
            color: metric.color,
            shape: metric.shape,
            unit: unitKey === null ? null : (i18n.global.t(unitKey) as string),
            value: productivityMetricValue(key, actual, props.granularity),
            referenceValue: productivityMetricValue(key, reference, props.granularity),
            referenceGap: productivityMetricGap(key, actual, reference, props.granularity),
            // This cell's figure broken down by camera, or nothing where the metric is not one a camera
            // reports. Formatted and gapped through the same functions the Section and reference figures
            // above go through, so the stack reads as one column of comparable numbers.
            //
            // The gap is against the SECTION figure, not the reference: a camera is part of this Section,
            // and what a reader wants is how far it sits from the whole. It is null where the Section
            // figure is withheld, and the camera figures beside that blank are the explanation for it.
            cameras:
                !camerasShown.value || !isCameraMetric(key)
                    ? []
                    : props.cameras.map((camera) => ({
                          ordinal: camera.ordinal,
                          sensorId: camera.sensorId,
                          color: camera.color,
                          hidden: props.hiddenSensorIds.includes(camera.sensorId),
                          label: key === namingMetric.value ? camera.label : null,
                          value: productivityMetricValue(key, camera[key], props.granularity),
                          gap: productivityMetricGap(key, camera[key], actual, props.granularity),
                      })),
        };
    })
);

// How far the measured figure sits from its norm. Null unless both are on screen and the norm is
// non-zero: a percentage of nothing is not a deviation, and quoting one against a hidden series
// would be reporting a comparison the reader cannot see either side of.
const waterDeviationPercent = computed(() => {
    if (!props.visible.water || !props.visible.waterNorm) return null;
    const actual = props.readout?.waterActual ?? null;
    const norm = props.readout?.waterNorm ?? null;
    if (actual === null || norm === null || norm === 0) return null;
    const percent = ((actual - norm) / norm) * 100;
    return Number.isFinite(percent) ? Math.round(percent) : null;
});

const formattedWaterDeviation = computed(() => (waterDeviationPercent.value === null ? PRODUCTIVITY_VALUE_PLACEHOLDER : (i18n.global.n(waterDeviationPercent.value, 'decimal') as string)));
</script>
